import { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Calendar,
  ChevronRight,
  Clock,
  MapPin,
  Instagram,
  ExternalLink,
  Phone,
} from "lucide-react";
import { EVENTS, type EventItem } from "./Events";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";

// ─── Colorful illustrated SVG icons (flat/3D style, no gray outlines) ───
function RestaurantsIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780340164199-6yn4hj.png"
      alt="Restaurants"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function ConcertsIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780338859810-lpw44o.png"
      alt="Concerts/Events"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function TemplesIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780338860170-7my59.png"
      alt="Temples"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function ShoppingIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780340164009-pppzhi.png"
      alt="Shopping"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function VenuesIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="16" width="32" height="24" rx="2" fill="#EC4899" opacity="0.15"/>
      <path d="M24 8 L8 16 L40 16 Z" fill="#EC4899" opacity="0.8"/>
      <rect x="20" y="24" width="8" height="16" rx="1" fill="#EC4899"/>
      <path d="M12 20 L16 20" stroke="#EC4899" strokeWidth="2" strokeLinecap="round"/>
      <path d="M32 20 L36 20" stroke="#EC4899" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function DecoratorsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M24 8 L32 16 L24 24 L16 16 Z" fill="#8B5CF6" opacity="0.8"/>
      <circle cx="16" cy="32" r="6" fill="#FCD34D"/>
      <circle cx="16" cy="32" r="3" fill="#F59E0B"/>
      <path d="M36 28 L28 36 L36 44 L44 36 Z" fill="#F472B6" opacity="0.9"/>
    </svg>
  );
}

function SweetTemptationsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Ice cream scoop */}
      <circle cx="24" cy="18" r="10" fill="#F9A8D4" opacity="0.9"/>
      <circle cx="24" cy="18" r="10" stroke="#EC4899" strokeWidth="1.5"/>
      {/* Cone */}
      <path d="M16 26 L24 42 L32 26 Z" fill="#F59E0B" opacity="0.85"/>
      <path d="M16 26 L24 42 L32 26 Z" stroke="#D97706" strokeWidth="1.5" strokeLinejoin="round"/>
      {/* Cone lines */}
      <line x1="20" y1="27" x2="24" y2="40" stroke="#D97706" strokeWidth="0.8" strokeLinecap="round"/>
      <line x1="28" y1="27" x2="24" y2="40" stroke="#D97706" strokeWidth="0.8" strokeLinecap="round"/>
      {/* Cherry on top */}
      <circle cx="24" cy="9" r="2.5" fill="#EF4444"/>
      <path d="M24 9 Q27 5 30 7" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      {/* Sprinkles */}
      <rect x="19" y="15" width="3" height="1.2" rx="0.6" fill="#8B5CF6" transform="rotate(-30 19 15)"/>
      <rect x="25" y="19" width="3" height="1.2" rx="0.6" fill="#F97316" transform="rotate(20 25 19)"/>
      <rect x="21" y="21" width="3" height="1.2" rx="0.6" fill="#06B6D4" transform="rotate(-10 21 21)"/>
    </svg>
  );
}

function CafeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Coffee cup */}
      <path d="M10 18 L12 38 Q12 42 16 42 L28 42 Q32 42 32 38 L34 18 Z" fill="#F59E0B" opacity="0.85"/>
      <path d="M10 18 L12 38 Q12 42 16 42 L28 42 Q32 42 32 38 L34 18 Z" stroke="#D97706" strokeWidth="1.5" strokeLinejoin="round"/>
      {/* Handle */}
      <path d="M34 22 Q42 22 41 29 Q40 35 33 34" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      {/* Coffee surface */}
      <ellipse cx="22" cy="18" rx="12" ry="2.5" fill="#78350F" opacity="0.7"/>
      {/* Steam */}
      <path d="M16 8 Q14 12 16 14" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
      <path d="M22 6 Q20 10 22 13" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
      <path d="M28 8 Q26 12 28 14" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
      {/* Saucer */}
      <ellipse cx="22" cy="44" rx="16" ry="2" fill="#D1D5DB"/>
    </svg>
  );
}

function InfluencersIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780338859925-bo6iq.png"
      alt="Influencers"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function ModelsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="14" r="7" fill="#f59e0b" opacity="0.85"/>
      <path d="M24 22 C15 22 10 28 10 38 L10 40 L38 40 L38 38 C38 28 33 22 24 22 Z" fill="#082f6d" opacity="0.85"/>
      <path d="M17 26 L24 40 L31 26" stroke="#51ba4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

function EventOrganizerIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect x="12" y="16" width="16" height="14" rx="3" fill="#9CA3AF" opacity="0.2"/>
      <path d="M28 18 L38 12 L38 36 L28 30 Z" fill="#9CA3AF" opacity="0.2" stroke="#9CA3AF" strokeWidth="1.5" strokeLinejoin="round"/>
      <rect x="12" y="20" width="7" height="6" rx="1" fill="#9CA3AF" opacity="0.15"/>
      <path d="M32 18 Q36 23 32 30" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.25"/>
      <path d="M34 16 Q40 23 34 32" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.18"/>
      <circle cx="17" cy="23" r="2" fill="#9CA3AF" opacity="0.2"/>
    </svg>
  );
}

function MakeupHairIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Mirror */}
      <ellipse cx="18" cy="18" rx="8" ry="10" fill="#F9A8D4" opacity="0.35"/>
      <ellipse cx="18" cy="18" rx="8" ry="10" stroke="#EC4899" strokeWidth="1.5"/>
      <ellipse cx="18" cy="18" rx="5" ry="7" fill="#FDF2F8" stroke="#EC4899" strokeWidth="0.8"/>
      {/* Mirror handle */}
      <rect x="16" y="28" width="4" height="8" rx="1.5" fill="#EC4899" opacity="0.7"/>
      {/* Lipstick */}
      <rect x="32" y="28" width="5" height="12" rx="2" fill="#EF4444" opacity="0.9"/>
      <rect x="31.5" y="26" width="6" height="4" rx="1.5" fill="#EA580C"/>
      <rect x="31" y="24.5" width="7" height="2.5" rx="1" fill="#FBBF24"/>
      {/* Hair brush */}
      <rect x="33" y="8" width="8" height="4" rx="2" fill="#8B5CF6" opacity="0.8" transform="rotate(-20 37 10)"/>
      <rect x="35" y="6" width="10" height="3" rx="1.5" fill="#A78BFA" opacity="0.7" transform="rotate(-20 40 7.5)"/>
      {/* Sparkles */}
      <path d="M6 6 L7 9 L10 10 L7 11 L6 14 L5 11 L2 10 L5 9 Z" fill="#F59E0B"/>
      <path d="M42 16 L42.5 17.5 L44 18 L42.5 18.5 L42 20 L41.5 18.5 L40 18 L41.5 17.5 Z" fill="#FBBF24"/>
    </svg>
  );
}

function SalonSpaIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Lotus/flower */}
      <path d="M24 8 Q28 14 24 20 Q20 14 24 8 Z" fill="#EC4899" opacity="0.6"/>
      <path d="M24 8 Q30 14 24 20 Q18 14 24 8 Z" fill="#F472B6" opacity="0.5"/>
      <path d="M24 8 Q32 16 24 22 Q16 16 24 8 Z" fill="#FBCFE8" opacity="0.4"/>
      <circle cx="24" cy="14" r="2.5" fill="#F59E0B"/>
      {/* Steam / aroma lines */}
      <path d="M9 22 Q9 16 13 12" stroke="#A78BFA" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
      <path d="M12 24 Q12 18 15 14" stroke="#A78BFA" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.4"/>
      <path d="M39 22 Q39 16 35 12" stroke="#A78BFA" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
      <path d="M36 24 Q36 18 33 14" stroke="#A78BFA" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.4"/>
      {/* Towel roll */}
      <rect x="14" y="32" width="20" height="10" rx="3" fill="#E5E7EB" stroke="#D1D5DB" strokeWidth="1"/>
      <ellipse cx="24" cy="32" rx="10" ry="2.5" fill="#F3F4F6" stroke="#D1D5DB" strokeWidth="0.8"/>
      {/* Stones */}
      <ellipse cx="18" cy="34" rx="2" ry="1.2" fill="#9CA3AF" opacity="0.5"/>
      <ellipse cx="24" cy="35" rx="2.5" ry="1.2" fill="#9CA3AF" opacity="0.5"/>
      <ellipse cx="30" cy="34" rx="2" ry="1.2" fill="#9CA3AF" opacity="0.5"/>
    </svg>
  );
}

function DJsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Headphones */}
      <path d="M14 30 Q14 22 24 18 Q34 22 34 30" stroke="#8B5CF6" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      <rect x="10" y="28" width="8" height="6" rx="3" fill="#8B5CF6" opacity="0.8"/>
      <rect x="30" y="28" width="8" height="6" rx="3" fill="#8B5CF6" opacity="0.8"/>
      {/* Headband */}
      <path d="M14 30 Q14 20 24 14 Q34 20 34 30" stroke="#8B5CF6" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.5"/>
      {/* Turntable / vinyl */}
      <circle cx="24" cy="34" r="8" fill="#1F2937" opacity="0.85"/>
      <circle cx="24" cy="34" r="3" fill="#F97316" opacity="0.9"/>
      <circle cx="24" cy="34" r="1" fill="#1F2937"/>
      {/* Tonearm */}
      <path d="M27 27 Q32 24 34 28" stroke="#E5E7EB" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      {/* Equalizer bars */}
      <rect x="6" y="26" width="2.5" height="6" rx="1" fill="#F59E0B" opacity="0.9"/>
      <rect x="9.5" y="23" width="2.5" height="9" rx="1" fill="#F97316" opacity="0.9"/>
      <rect x="13" y="20" width="2.5" height="12" rx="1" fill="#EF4444" opacity="0.9"/>
      <rect x="34" y="24" width="2.5" height="8" rx="1" fill="#F59E0B" opacity="0.9"/>
      <rect x="37.5" y="21" width="2.5" height="11" rx="1" fill="#F97316" opacity="0.9"/>
      <rect x="41" y="26" width="2.5" height="6" rx="1" fill="#EF4444" opacity="0.9"/>
    </svg>
  );
}

function ViewAllIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="18" fill="#F97316" opacity="0.15"/>
      <rect x="12" y="12" width="10" height="10" rx="2" fill="#F97316"/>
      <rect x="26" y="12" width="10" height="10" rx="2" fill="#FB923C"/>
      <rect x="12" y="26" width="10" height="10" rx="2" fill="#FB923C"/>
      <rect x="26" y="26" width="10" height="10" rx="2" fill="#FDBA74"/>
      <path d="M20 17h-4M20 31h-4M34 17h-4M34 31h-4" stroke="#FFF7ED" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function MoviesIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="20" width="32" height="20" rx="3" fill="#8B5CF6" opacity="0.15"/>
      <rect x="8" y="20" width="32" height="20" rx="3" stroke="#8B5CF6" strokeWidth="2"/>
      <path d="M8 20 L10.5 13 L40 13 L37.5 20 Z" fill="#8B5CF6" opacity="0.3" stroke="#8B5CF6" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M16 13 L13.5 20 M24 13 L21.5 20 M32 13 L29.5 20" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function KidsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="16" r="8" fill="#FCD34D" opacity="0.9" stroke="#F59E0B" strokeWidth="1.5"/>
      <circle cx="20" cy="14" r="1.5" fill="#1F2937"/>
      <circle cx="28" cy="14" r="1.5" fill="#1F2937"/>
      <path d="M20 19 Q24 23 28 19" stroke="#1F2937" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <rect x="18" y="24" width="12" height="14" rx="3" fill="#60A5FA" opacity="0.8" stroke="#3B82F6" strokeWidth="1.5"/>
      <rect x="14" y="26" width="6" height="8" rx="2" fill="#F472B6" opacity="0.8" stroke="#EC4899" strokeWidth="1"/>
      <rect x="28" y="26" width="6" height="8" rx="2" fill="#F472B6" opacity="0.8" stroke="#EC4899" strokeWidth="1"/>
      <circle cx="24" cy="32" r="2" fill="#F59E0B"/>
    </svg>
  );
}

function DatingMatrimonialIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M24 38 C12 28 6 22 6 16 C6 11 10 8 14 8 C17 8 20 10 24 14 C28 10 31 8 34 8 C38 8 42 11 42 16 C42 22 36 28 24 38 Z" fill="#EC4899" opacity="0.85" stroke="#BE185D" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M24 38 C12 28 6 22 6 16 C6 11 10 8 14 8 C17 8 20 10 24 14" fill="#F472B6" opacity="0.6"/>
    </svg>
  );
}

function NonProfitOrgIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="18" fill="#14B8A6" opacity="0.12"/>
      <path d="M24 36 C14 28 8 22 8 16 C8 12 12 10 16 10 C19 10 22 12 24 15 C26 12 29 10 32 10 C36 10 40 12 40 16 C40 22 34 28 24 36 Z" fill="#14B8A6" opacity="0.8" stroke="#0D9488" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M24 36 C14 28 8 22 8 16 C8 12 12 10 16 10 C19 10 22 12 24 15" fill="#5EEAD4" opacity="0.5"/>
      <circle cx="16" cy="16" r="2" fill="#F59E0B" opacity="0.8"/>
      <circle cx="32" cy="16" r="2" fill="#F59E0B" opacity="0.8"/>
    </svg>
  );
}

function YouthIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="18" fill="#3B82F6" opacity="0.1"/>
      <path d="M24 6 L27 17 L38 17 L29 24 L32 35 L24 28 L16 35 L19 24 L10 17 L21 17 Z" fill="#3B82F6" opacity="0.8" stroke="#2563EB" strokeWidth="1.5" strokeLinejoin="round"/>
      <circle cx="24" cy="22" r="3" fill="#FBBF24" opacity="0.9"/>
    </svg>
  );
}

function WomenIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="18" fill="#EC4899" opacity="0.1"/>
      <circle cx="16" cy="14" r="5" fill="#EC4899" opacity="0.7" stroke="#BE185D" strokeWidth="1.5"/>
      <circle cx="32" cy="14" r="5" fill="#F472B6" opacity="0.7" stroke="#BE185D" strokeWidth="1.5"/>
      <path d="M16 19 L16 28 L24 36 L32 28 L32 19" stroke="#EC4899" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="#EC4899" opacity="0.15"/>
      <circle cx="16" cy="14" r="1.5" fill="#FDF2F8"/>
      <circle cx="32" cy="14" r="1.5" fill="#FDF2F8"/>
    </svg>
  );
}

function CricketIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Cricket ball */}
      <circle cx="16" cy="28" r="7" fill="#EF4444" opacity="0.9" stroke="#B91C1C" strokeWidth="1.5"/>
      <path d="M16 21 Q16 28 16 35" stroke="#B91C1C" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M11 25 Q16 28 21 25" stroke="#B91C1C" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M11 31 Q16 28 21 31" stroke="#B91C1C" strokeWidth="1.2" strokeLinecap="round"/>
      {/* Cricket bat */}
      <rect x="28" y="8" width="4" height="22" rx="1.5" fill="#D97706" opacity="0.9"/>
      <path d="M26 8 L34 8 L32 4 L28 4 Z" fill="#F59E0B" opacity="0.9" stroke="#D97706" strokeWidth="1"/>
      <rect x="26" y="30" width="8" height="3" rx="1" fill="#D97706" opacity="0.8"/>
      {/* Wickets/stumps */}
      <rect x="36" y="32" width="1.5" height="10" rx="0.75" fill="#92400E" opacity="0.7"/>
      <rect x="39" y="32" width="1.5" height="10" rx="0.75" fill="#92400E" opacity="0.7"/>
      <rect x="42" y="32" width="1.5" height="10" rx="0.75" fill="#92400E" opacity="0.7"/>
      <rect x="35" y="30" width="10" height="1.5" rx="0.75" fill="#92400E" opacity="0.5"/>
    </svg>
  );
}

function TailoringIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Scissors */}
      <circle cx="12" cy="30" r="4" fill="#DB2777" opacity="0.15" stroke="#DB2777" strokeWidth="1.5"/>
      <circle cx="12" cy="16" r="4" fill="#DB2777" opacity="0.15" stroke="#DB2777" strokeWidth="1.5"/>
      <path d="M15 18 L34 32" stroke="#DB2777" strokeWidth="2" strokeLinecap="round"/>
      <path d="M15 28 L34 14" stroke="#DB2777" strokeWidth="2" strokeLinecap="round"/>
      {/* Thread spool */}
      <circle cx="36" cy="12" r="5" fill="#F59E0B" opacity="0.15" stroke="#F59E0B" strokeWidth="1.5"/>
      <path d="M36 8 L36 16 M32 12 L40 12" stroke="#F59E0B" strokeWidth="1.2" strokeLinecap="round"/>
      {/* Needle & thread */}
      <path d="M20 38 Q28 34 36 38" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <circle cx="20" cy="38" r="1.5" fill="#8B5CF6"/>
      <circle cx="36" cy="38" r="1.5" fill="#8B5CF6"/>
    </svg>
  );
}

function AutomotiveServicesIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Car body */}
      <path d="M6 30 L9 20 Q10 17 14 17 L34 17 Q38 17 39 20 L42 30" stroke="#082f6d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="#082f6d" fillOpacity="0.12"/>
      <rect x="4" y="30" width="40" height="7" rx="2.5" fill="#082f6d" opacity="0.85"/>
      {/* Wheels */}
      <circle cx="14" cy="38" r="4" fill="#1f2937" stroke="#082f6d" strokeWidth="1.2"/>
      <circle cx="34" cy="38" r="4" fill="#1f2937" stroke="#082f6d" strokeWidth="1.2"/>
      {/* Wrench */}
      <path d="M30 8 L38 16" stroke="#f59e0b" strokeWidth="2.4" strokeLinecap="round"/>
      <circle cx="29" cy="7" r="4" fill="none" stroke="#f59e0b" strokeWidth="2"/>
    </svg>
  );
}

function InsuranceIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="18" fill="#f59e0b" opacity="0.1"/>
      <path d="M24 8 L36 13 V23 C36 31 30.5 36.5 24 39 C17.5 36.5 12 31 12 23 V13 Z" stroke="#082f6d" strokeWidth="2" strokeLinejoin="round" fill="none"/>
      <path d="M18 24 L22.5 28.5 L31 19" stroke="#51ba4f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

function OthersIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="16" cy="16" r="6" fill="#8B5CF6" opacity="0.8"/>
      <circle cx="32" cy="16" r="6" fill="#EC4899" opacity="0.8"/>
      <circle cx="24" cy="32" r="6" fill="#F97316" opacity="0.8"/>
      <circle cx="16" cy="16" r="2" fill="#C4B5FD"/>
      <circle cx="32" cy="16" r="2" fill="#FBCFE8"/>
      <circle cx="24" cy="32" r="2" fill="#FED7AA"/>
    </svg>
  );
}

function JobsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Briefcase */}
      <rect x="10" y="18" width="28" height="20" rx="3" fill="#082f6d" opacity="0.85"/>
      <path d="M10 26 H38" stroke="#51ba4f" strokeWidth="2" strokeLinecap="round"/>
      <path d="M19 18 V15 Q19 12 22 12 H26 Q29 12 29 15 V18" stroke="#082f6d" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      <rect x="21" y="24" width="6" height="4" rx="1" fill="#f59e0b"/>
      {/* Sparkle */}
      <path d="M38 8 L38.8 10.2 L41 11 L38.8 11.8 L38 14 L37.2 11.8 L35 11 L37.2 10.2 Z" fill="#f59e0b"/>
    </svg>
  );
}

function BartendersIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M16 8 L32 8 L28 24 L20 24 Z" fill="#06B6D4" opacity="0.15" stroke="#06B6D4" strokeWidth="2" strokeLinejoin="round"/>
      <ellipse cx="24" cy="8" rx="8" ry="2" fill="#06B6D4" opacity="0.5"/>
      <rect x="20" y="24" width="8" height="2" rx="1" fill="#06B6D4" opacity="0.6"/>
      <rect x="22" y="26" width="4" height="10" rx="1" fill="#06B6D4" opacity="0.4"/>
      <ellipse cx="24" cy="38" rx="6" ry="2" fill="#06B6D4" opacity="0.25"/>
      <circle cx="18" cy="16" r="2" fill="#F59E0B" opacity="0.8"/>
      <circle cx="30" cy="14" r="1.5" fill="#EF4444" opacity="0.7"/>
    </svg>
  );
}

function BakersIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M16 36 Q16 24 24 20 Q32 24 32 36 Z" fill="#F59E0B" opacity="0.85" stroke="#D97706" strokeWidth="1.5"/>
      <ellipse cx="24" cy="18" rx="10" ry="4" fill="#FCD34D" opacity="0.9" stroke="#F59E0B" strokeWidth="1.5"/>
      <circle cx="20" cy="16" r="1.5" fill="#F59E0B"/>
      <circle cx="24" cy="15" r="1.5" fill="#F59E0B"/>
      <circle cx="28" cy="16" r="1.5" fill="#F59E0B"/>
      <path d="M14 36 Q14 30 18 28" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <path d="M34 36 Q34 30 30 28" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    </svg>
  );
}

function CateringOnlyIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="24" cy="22" rx="16" ry="3" fill="#8B5CF6" opacity="0.2"/>
      <ellipse cx="24" cy="20" rx="14" ry="8" fill="#8B5CF6" opacity="0.15" stroke="#8B5CF6" strokeWidth="2"/>
      <ellipse cx="24" cy="20" rx="10" ry="5" fill="#C4B5FD" opacity="0.5"/>
      <circle cx="18" cy="18" r="2.5" fill="#F97316" opacity="0.8"/>
      <circle cx="24" cy="17" r="2.5" fill="#EF4444" opacity="0.8"/>
      <circle cx="30" cy="18" r="2.5" fill="#16A34A" opacity="0.8"/>
      <rect x="20" y="28" width="8" height="2" rx="1" fill="#8B5CF6" opacity="0.5"/>
    </svg>
  );
}

function HomeServicesIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M8 22 L24 8 L40 22" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 22 V38 H36 V22" stroke="#10B981" strokeWidth="2" strokeLinejoin="round" fill="#10B981" opacity="0.1"/>
      <rect x="21" y="30" width="6" height="8" rx="1" fill="#10B981" opacity="0.7"/>
      <path d="M30 14 L34 10 L38 14" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <circle cx="34" cy="10" r="1.5" fill="#F59E0B"/>
    </svg>
  );
}

function TravelAgentsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M8 34 L16 22 L24 28 L32 18 L40 30" stroke="#EC4899" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <circle cx="16" cy="22" r="2" fill="#EC4899" opacity="0.6"/>
      <circle cx="32" cy="18" r="2" fill="#EC4899" opacity="0.6"/>
      <path d="M36 10 L40 6 L44 10 L40 14 Z" fill="#F97316" opacity="0.8" stroke="#EA580C" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M40 14 L40 20" stroke="#EA580C" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M36 18 L44 18" stroke="#EA580C" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function PerformingArtistsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="16" cy="18" r="8" fill="#8B5CF6" opacity="0.15" stroke="#8B5CF6" strokeWidth="2"/>
      <circle cx="32" cy="18" r="8" fill="#EC4899" opacity="0.15" stroke="#EC4899" strokeWidth="2"/>
      <circle cx="16" cy="18" r="2.5" fill="#8B5CF6" opacity="0.7"/>
      <circle cx="32" cy="18" r="2.5" fill="#EC4899" opacity="0.7"/>
      <path d="M12 26 Q16 30 20 26" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <path d="M28 26 Q32 30 36 26" stroke="#EC4899" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <path d="M16 26 L16 34" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M32 26 L32 34" stroke="#EC4899" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function MediaIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect x="6" y="14" width="28" height="22" rx="3" fill="#F97316" opacity="0.15" stroke="#F97316" strokeWidth="2"/>
      <rect x="12" y="10" width="8" height="4" rx="1.5" fill="#F97316" opacity="0.5" stroke="#F97316" strokeWidth="1.5"/>
      <circle cx="20" cy="25" r="7" stroke="#F97316" strokeWidth="2"/>
      <circle cx="20" cy="25" r="3" fill="#F97316" opacity="0.4"/>
      <path d="M36 18 L44 24 L36 30 Z" fill="#F97316" opacity="0.7" stroke="#F97316" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="34" y1="18" x2="34" y2="30" stroke="#F97316" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function SeniorCitizensIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="14" r="7" fill="#FCD34D" opacity="0.9" stroke="#F59E0B" strokeWidth="1.5"/>
      <circle cx="21" cy="12" r="1.2" fill="#1F2937"/>
      <circle cx="27" cy="12" r="1.2" fill="#1F2937"/>
      <path d="M21 17 Q24 20 27 17" stroke="#1F2937" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
      <path d="M16 22 Q24 16 32 22" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <rect x="18" y="24" width="12" height="14" rx="3" fill="#60A5FA" opacity="0.7" stroke="#3B82F6" strokeWidth="1.5"/>
      <path d="M14 36 L14 40 M34 36 L34 40" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
      <rect x="12" y="38" width="6" height="2" rx="1" fill="#9CA3AF" opacity="0.6"/>
      <rect x="30" y="38" width="6" height="2" rx="1" fill="#9CA3AF" opacity="0.6"/>
    </svg>
  );
}

function StartUpsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Rocket body */}
      <path d="M24 6 L18 22 L24 28 L30 22 Z" fill="#0891b2" opacity="0.85" stroke="#0e7490" strokeWidth="1.5" strokeLinejoin="round"/>
      {/* Rocket nose */}
      <path d="M24 6 L22 10 L26 10 Z" fill="#06b6d4" opacity="0.9"/>
      {/* Window */}
      <circle cx="24" cy="16" r="2.5" fill="#cffafe" stroke="#0e7490" strokeWidth="1"/>
      {/* Fins */}
      <path d="M18 22 L12 28 L18 26 Z" fill="#06b6d4" opacity="0.8" stroke="#0e7490" strokeWidth="1" strokeLinejoin="round"/>
      <path d="M30 22 L36 28 L30 26 Z" fill="#06b6d4" opacity="0.8" stroke="#0e7490" strokeWidth="1" strokeLinejoin="round"/>
      {/* Flame */}
      <path d="M21 28 Q24 38 27 28" fill="#f97316" opacity="0.9"/>
      <path d="M22.5 28 Q24 34 25.5 28" fill="#fbbf24" opacity="0.9"/>
      {/* Stars */}
      <path d="M8 8 L9 11 L12 12 L9 13 L8 16 L7 13 L4 12 L7 11 Z" fill="#f59e0b"/>
      <path d="M40 10 L40.8 12.2 L43 13 L40.8 13.8 L40 16 L39.2 13.8 L37 13 L39.2 12.2 Z" fill="#fbbf24"/>
    </svg>
  );
}

function ITConsultingIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect x="6" y="10" width="24" height="18" rx="3" fill="#3B82F6" opacity="0.15" stroke="#3B82F6" strokeWidth="2"/>
      <rect x="6" y="10" width="24" height="4" rx="2" fill="#3B82F6" opacity="0.3"/>
      <circle cx="10" cy="12" r="1" fill="#EF4444"/>
      <circle cx="14" cy="12" r="1" fill="#F59E0B"/>
      <circle cx="18" cy="12" r="1" fill="#16A34A"/>
      <rect x="10" y="18" width="16" height="1.5" rx="0.75" fill="#3B82F6" opacity="0.3"/>
      <rect x="10" y="22" width="12" height="1.5" rx="0.75" fill="#3B82F6" opacity="0.3"/>
      <rect x="34" y="14" width="8" height="14" rx="2" fill="#8B5CF6" opacity="0.15" stroke="#8B5CF6" strokeWidth="1.5"/>
      <rect x="35.5" y="16" width="5" height="6" rx="1" fill="#8B5CF6" opacity="0.4"/>
      <rect x="35.5" y="24" width="5" height="2" rx="0.75" fill="#8B5CF6" opacity="0.3"/>
      <path d="M18 32 L18 38 L28 38 L28 32" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <rect x="16" y="36" width="14" height="2" rx="1" fill="#3B82F6" opacity="0.4"/>
    </svg>
  );
}

function HealthWellnessIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780340319237-4x8nm.png"
      alt="Health & Wellness"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function PhotoVideoIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Camera body */}
      <rect x="6" y="16" width="26" height="20" rx="3" fill="#EC4899" opacity="0.15"/>
      <rect x="6" y="16" width="26" height="20" rx="3" stroke="#EC4899" strokeWidth="2"/>
      {/* Camera bump */}
      <rect x="14" y="12" width="7" height="4" rx="1.5" fill="#EC4899" opacity="0.5" stroke="#EC4899" strokeWidth="1.5"/>
      {/* Lens */}
      <circle cx="19" cy="26" r="6" stroke="#EC4899" strokeWidth="2"/>
      <circle cx="19" cy="26" r="3" fill="#EC4899" opacity="0.4"/>
      {/* Video play arrow */}
      <path d="M35 19 L44 24 L35 29 Z" fill="#EC4899" opacity="0.8" stroke="#EC4899" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="33" y1="19" x2="33" y2="29" stroke="#EC4899" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function GroceryIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Cart handle */}
      <path d="M6 10 H11 L15 28 H36 L40 16 H14" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      {/* Cart body fill */}
      <path d="M15 28 H36 L40 16 H14 Z" fill="#16A34A" opacity="0.12"/>
      {/* Wheels */}
      <circle cx="19" cy="33" r="2.5" fill="#16A34A" opacity="0.7" stroke="#16A34A" strokeWidth="1.5"/>
      <circle cx="32" cy="33" r="2.5" fill="#16A34A" opacity="0.7" stroke="#16A34A" strokeWidth="1.5"/>
      {/* Produce */}
      <circle cx="21" cy="23" r="2.5" fill="#F59E0B" opacity="0.9"/>
      <circle cx="28" cy="22" r="2.5" fill="#EF4444" opacity="0.9"/>
      <circle cx="34" cy="23" r="2" fill="#16A34A" opacity="0.9"/>
    </svg>
  );
}

 const categories = [
  { name: "Restaurants", icon: RestaurantsIcon, active: true, href: "/restaurants" },
  { name: "Concerts/Events", icon: ConcertsIcon, active: false, href: "/concerts" },
  { name: "Temples", icon: TemplesIcon, active: false, href: "/temples" },
  { name: "Shopping", icon: ShoppingIcon, active: false, href: "/shopping" },
  { name: "Dessert Shops", icon: SweetTemptationsIcon, active: false, href: "/dessert-shops" },
  { name: "Cafe", icon: CafeIcon, active: false, href: "/cafe" },
  { name: "Health & Wellness", icon: HealthWellnessIcon, active: false, href: "/health-wellness" },
  { name: "Grocery", icon: GroceryIcon, active: false, href: "/grocery" },
  { name: "Photo/Video", icon: PhotoVideoIcon, active: false, href: "/photo-video" },
  { name: "Movies", icon: MoviesIcon, active: false, href: "/movies" },
  { name: "Party Venues", icon: VenuesIcon, active: false, href: "/party-venues" },
   { name: "Event Planning", icon: DecoratorsIcon, active: false, href: "/event-management" },
  { name: "Marketing/production", icon: EventOrganizerIcon, active: false, href: "/event-production" },
  { name: "Influencers", icon: InfluencersIcon, active: false, href: "/influencers" },
  { name: "Models", icon: ModelsIcon, active: false, href: "/models" },
  { name: "Beauty Services", icon: MakeupHairIcon, active: false, href: "/BeautyServices" },
  { name: "Salon/Spa", icon: SalonSpaIcon, active: false, href: "/salon-spa" },
  // Hookah Lounges removed from home categories — still available as a type on the restaurants page
  { name: "DJ's", icon: DJsIcon, active: false, href: "/djs" },
  { name: "Kids", icon: KidsIcon, active: false, href: "/kids" },
  { name: "Catering", icon: CateringOnlyIcon, active: false, href: "/catering-only" },
  { name: "Travel Agents", icon: TravelAgentsIcon, active: false, href: "/travel-agents" },
  { name: "Performing Artists", icon: PerformingArtistsIcon, active: false, href: "/performing-artists" },
  { name: "Media", icon: MediaIcon, active: false, href: "/media" },
  { name: "Senior Citizens", icon: SeniorCitizensIcon, active: false, href: "/senior-citizen" },
  { name: "Start Ups", icon: StartUpsIcon, active: false, href: "/start-ups" },
  { name: "Consulting", icon: ITConsultingIcon, active: false, href: "/it-consulting" },
  { name: "Non Profit Org", icon: NonProfitOrgIcon, active: false, href: "/non-profit-org" },
  { name: "Youth", icon: YouthIcon, active: false, href: "/youth" },
  { name: "Women", icon: WomenIcon, active: false, href: "/women" },
  { name: "Cricket", icon: CricketIcon, active: false, href: "/cricket" },
  { name: "Others", icon: OthersIcon, active: false, href: "/others" },
  { name: "Home Services", icon: HomeServicesIcon, active: false, href: "/home-services" },
  { name: "Tailoring", icon: TailoringIcon, active: false, href: "/tailoring" },
  { name: "Automotive Services", icon: AutomotiveServicesIcon, active: false, href: "/automotive-services" },
  { name: "Insurance", icon: InsuranceIcon, active: false, href: "/insurance" },
  { name: "Jobs", icon: JobsIcon, active: false, href: "/jobs" },
];

// Editable fields for a category card (icon stays the built-in SVG; name/link are editable).
const CATEGORY_FIELDS: FieldDef[] = [
  { key: "name", label: "Category name", type: "text", required: true },
  { key: "href", label: "Link (path, e.g. /restaurants)", type: "text" },
];

// Helper: parse ISO date string to local Date (returns null when no valid date is given)
function parseLocal(iso?: string): Date | null {
  if (!iso || typeof iso !== "string") return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function startOfToday(): Date {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Derive upcoming events from the canonical EVENTS list (same source as the Events page)
// Undated announcements (no `date`) are excluded from this date-sorted rail.
function useUpcomingEvents(): EventItem[] {
  return useMemo(() => {
    const today = startOfToday();
    const endOf = (e: EventItem) => parseLocal(e.endDate || e.date);
    return EVENTS
      .filter((e) => {
        const end = endOf(e);
        return end !== null && end >= today;
      })
      .sort((a, b) => {
        const aTime = parseLocal(a.date)?.getTime() ?? Infinity;
        const bTime = parseLocal(b.date)?.getTime() ?? Infinity;
        return aTime - bTime;
      });
  }, []);
}

function CategoryCard({
  category,
  index,
}: {
  category: (typeof categories)[0];
  index: number;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = category.icon;

  const cardContent = (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.04 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative flex flex-col items-center bg-white rounded-[10px] border border-[#e9edf3] hover:shadow-md transition-all duration-300 group"
      style={{
        fontFamily: "'Poppins', sans-serif",
        minHeight: "76px",
        padding: "6px 4px 4px",
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
        gap: "1px",
      }}
    >
      {'noIcon' in category && category.noIcon ? (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-[10px] opacity-25 pointer-events-none">
          <Icon className="w-[56px] h-[56px]" />
        </div>
      ) : (
        <div className="flex items-center justify-center transition-transform duration-300 group-hover:scale-110 flex-shrink-0">
          <Icon className="w-[40px] h-[40px]" />
        </div>
      )}
      <span
        className="font-bold leading-[1.15] text-center transition-colors duration-300 overflow-hidden text-ellipsis"
        style={{
          color: "#111827",
          maxWidth: "100%",
          fontSize: ('noIcon' in category && category.noIcon) ? '8px' : '10px',
          ...(('noIcon' in category && category.noIcon) ? { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 } : {}),
        }}
      >
        {'noIcon' in category && category.noIcon
          ? category.name.split('/').map((word, i) => <span key={i} className="block leading-[1.3]">{word.trim()}</span>)
          : category.name === "Concerts/Events" ? <><span className="block leading-[1.3]">Concerts/</span><span className="block leading-[1.3]">Events</span></> : category.name === "Marketing/production" ? <><span className="block leading-[1.3]">Marketing/</span><span className="block leading-[1.3]">production</span></> : category.name
        }
      </span>
      {(category.active || isHovered) && (
        <div
          className="absolute left-1/2 -translate-x-1/2 rounded-full bg-[#f97316]"
          style={{ bottom: "-1px", width: "40px", height: "2px" }}
        />
      )}
    </motion.div>
  );

  if (category.href) {
    return (
      <Link to={category.href} className="block">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

function ViewAllCard({ index }: { index: number }) {
  return (
    <Link to="/categories">
      <motion.button
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.4, delay: index * 0.04 }}
        className="relative flex flex-col items-center bg-[#dbeafe] rounded-[10px] border-2 border-[#93c5fd] hover:shadow-lg hover:border-[#60a5fa] transition-all duration-300 group"
        style={{
          fontFamily: "'Poppins', sans-serif",
          width: "80px",
        minHeight: "76px",
          padding: "6px 4px 4px",
          boxShadow: "0 4px 14px rgba(8, 47, 109, 0.18)",
          gap: "1px",
        }}
      >
        <div className="flex items-center justify-center transition-transform duration-300 group-hover:scale-110 flex-shrink-0">
          <ViewAllIcon className="w-[40px] h-[40px]" />
        </div>
        <span className="text-[13px] font-bold leading-[1.1] text-center text-[#111827]">
          View All
        </span>
      </motion.button>
    </Link>
  );
}

function EventCard({ event }: { event: EventItem }) {
  const s = parseLocal(event.date);
  const dateStr = s ? `${MONTHS[s.getMonth()]} ${s.getDate()}, ${s.getFullYear()}` : "";

  const cardBody = (
    <>
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={event.image}
          alt={event.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          draggable={false}
        />
      </div>
      <div className="p-2.5">
        <h4 className="text-[12px] font-semibold text-gray-900 leading-tight mb-1.5 line-clamp-2">
          {event.name}
        </h4>
        <div className="flex items-center gap-2 text-[10px] text-gray-500 mb-1">
          <span className="flex items-center gap-0.5">
            <Calendar className="w-3 h-3" />
            {dateStr}
          </span>
          {event.time && (
            <span className="flex items-center gap-0.5">
              <Clock className="w-3 h-3" />
              {event.time}
            </span>
          )}
        </div>
        <span className="flex items-center gap-0.5 text-[10px] text-gray-500">
          <MapPin className="w-3 h-3" />
          {event.venue}, {event.city}, TX
        </span>
        {event.featuredBy && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="text-[9px] font-semibold text-pink-600 bg-pink-50 px-1.5 py-0.5 rounded-full">
              Featured by {event.featuredBy}
            </span>
            {event.instagramUrl && (
              <a
                href={event.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gradient-to-br from-pink-500 to-orange-400 text-white hover:scale-110 transition-transform"
                title={`@${event.featuredBy} on Instagram`}
              >
                <Instagram className="w-2.5 h-2.5" />
              </a>
            )}
          </div>
        )}
      </div>

      {/* CTA row — Get Tickets / Contact, same actions as the full Events page cards */}
      {(event.link || event.contactUrl || event.contact) && (
        <div className="px-2.5 pb-2.5 pt-0.5 flex gap-1.5">
          {event.link && (
            <a
              href={event.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex-1 flex items-center justify-center gap-1 bg-white border border-orange-200 text-orange-600 hover:bg-orange-50 text-[10px] font-semibold py-1.5 rounded-md transition-colors whitespace-nowrap"
            >
              <ExternalLink className="w-3 h-3" /> Get Tickets
            </a>
          )}
          {event.contactUrl && (
            <a
              href={event.contactUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex-1 flex items-center justify-center gap-1 bg-[#51ba4f] border border-[#51ba4f] text-white hover:bg-[#459f43] text-[10px] font-semibold py-1.5 rounded-md transition-colors whitespace-nowrap"
            >
              <Phone className="w-3 h-3" /> Call
            </a>
          )}
          {event.contact && !event.contactUrl && (
            <a
              href={`tel:${event.contact.replace(/[^\d+]/g, "")}`}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 flex items-center justify-center gap-1 bg-[#51ba4f] border border-[#51ba4f] text-white hover:bg-[#459f43] text-[10px] font-semibold py-1.5 rounded-md transition-colors whitespace-nowrap"
            >
              <Phone className="w-3 h-3" /> Call
            </a>
          )}
        </div>
      )}
    </>
  );

  const className =
    "flex-shrink-0 w-[210px] bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group cursor-pointer select-none";
  const style = { fontFamily: "'Poppins', sans-serif" } as const;

  return (
    <div className={className} style={style}>
      {cardBody}
    </div>
  );
}

function AutoScrollEvents({ events }: { events: EventItem[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isPausedRef = useRef(false);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let animationId: number;
    let scrollPos = 0;
    const speed = 0.5;

    const animate = () => {
      if (!container) return;
      // Skip advancing while paused (hovered) but keep the RAF loop alive so it
      // resumes smoothly the instant the mouse leaves — no restart glitch.
      if (!isPausedRef.current) {
        scrollPos += speed;
        const maxScroll = container.scrollWidth / 2;
        if (scrollPos >= maxScroll) {
          scrollPos = 0;
        }
        container.scrollLeft = scrollPos;
      } else {
        // Keep scrollPos in sync in case the user manually scrolled while paused
        scrollPos = container.scrollLeft;
      }
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  const allEvents = [...events, ...events];

  return (
    <div
      className="relative -mx-1 px-1"
      onMouseEnter={() => { isPausedRef.current = true; }}
      onMouseLeave={() => { isPausedRef.current = false; }}
      onTouchStart={() => { isPausedRef.current = true; }}
      onTouchEnd={() => { isPausedRef.current = false; }}
    >
      <div
        ref={scrollRef}
        className="flex items-start gap-3 overflow-x-auto pb-5 scrollbar-none"
        style={{ scrollBehavior: "auto" }}
      >
        {allEvents.map((event, i) => (
          <EventCard key={`${event.id}-${i}`} event={event} />
        ))}
      </div>
    </div>
  );
}

export default function HomeSections() {
  const upcomingEvents = useUpcomingEvents();
  const [seoExpanded, setSeoExpanded] = useState(false);
  // Categories are fully admin-managed: hide/unhide, edit, delete/restore, and
  // drag-reorder — all via the mc_home_categories store. Public sees only the
  // visible ones; every change persists and reflects on the public site.
  const catMc = useManagedCollection<(typeof categories)[number]>(
    "home_categories",
    categories,
    (c) => slugify(c.name)
  );

  return (
    <section className="bg-gray-50/50 py-6">
      <div className="page-container">
        {/* SEO Intro Section — above-the-fold content with primary keywords */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5 }}
          className="mb-8 bg-white rounded-lg p-6 border border-gray-100"
          style={{ fontFamily: "'Lora', serif" }}
        >
          <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-3">
            Dallas Desi Scoop – Your Dallas Desi Guide
          </h2>
          <div
            className={
              seoExpanded
                ? "md:!line-clamp-none"
                : "line-clamp-3 md:line-clamp-none"
            }
          >
            <p className="text-[13px] md:text-[14px] text-gray-600 leading-relaxed mb-3">
              Dallas Desi Scoop is your local guide to the best <strong className="font-bold text-gray-900">Indian, Pakistani, Mediterranean, and Desi businesses</strong>, restaurants, events, shopping, and services across <strong className="font-bold text-gray-900">Dallas–Fort Worth</strong>. Discover great places to eat, exciting events, local shopping, and trusted businesses in Dallas, Frisco, Plano, Irving, McKinney, Carrollton, Richardson, and throughout the DFW metroplex.
            </p>
            <p className="text-[13px] md:text-[14px] text-gray-600 leading-relaxed mb-3">
              Explore <Link to="/restaurants" className="text-gray-900 hover:text-gray-700 font-bold">Indian and Pakistani restaurants</Link>, <strong className="font-bold text-gray-900">Mediterranean and Middle Eastern food</strong>, <strong className="font-bold text-gray-900">Desi grocery stores</strong>, <Link to="/events" className="text-gray-900 hover:text-gray-700 font-bold">cultural events and concerts</Link>, <Link to="/shopping" className="text-gray-900 hover:text-gray-700 font-bold">shopping, clothing, and jewelry</Link>, wedding vendors, professional services, and connect with local services from weddings to finance to healthcare.
            </p>
            <p className="text-[13px] md:text-[14px] text-gray-600 leading-relaxed">
              Whether you're looking for <strong className="font-bold text-gray-900">Indian food in Dallas</strong>, <strong className="font-bold text-gray-900">Pakistani restaurants in DFW</strong>, <strong className="font-bold text-gray-900">Mediterranean restaurants in Frisco</strong>, <strong className="font-bold text-gray-900">Desi events this weekend</strong>, <strong className="font-bold text-gray-900">Indian grocery stores</strong>, or <strong className="font-bold text-gray-900">local Desi businesses</strong>, Dallas Desi Scoop helps you find what's happening around you. For in-depth coverage, read our <Link to="/blog" className="text-gray-900 hover:text-gray-700 font-bold">DFW Desi guides and community blog</Link>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSeoExpanded((v) => !v)}
            className="md:hidden mt-1 text-[13px] font-semibold text-orange-600 hover:text-orange-700"
          >
            {seoExpanded ? "Read less" : "Read more"}
          </button>
        </motion.div>

        {/* Main content — full width (social widgets moved to Our Story page) */}
        <div>
          {/* LEFT COLUMN */}
          <div className="min-w-0 order-1">
            {/* Explore Categories */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5 }}
              className="mb-6"
            >
              <div className="flex items-center justify-between mb-3">
                <h2
                  className="text-[16px] md:text-[18px] font-bold text-gray-900"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Explore Categories
                </h2>
              </div>
              <div className="category-grid">
                {catMc.items.map((cat, i) => (
                  <AdminItem key={cat._key} mc={catMc} item={cat} fields={CATEGORY_FIELDS} title="Category">
                    <CategoryCard category={cat as any} index={i} />
                  </AdminItem>
                ))}
                <ViewAllCard index={catMc.items.length} />
              </div>
            </motion.div>

            {/* Upcoming Events */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center justify-between mb-3">
                <h2
                  className="text-[16px] md:text-[18px] font-bold text-gray-900"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Upcoming Events/Concerts
                </h2>
                <Link
                  to="/events"
                  className="text-[11px] md:text-[12px] font-medium text-orange-500 hover:text-orange-600 flex items-center gap-0.5 transition-colors"
                >
                  View All
                  <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              <AutoScrollEvents events={upcomingEvents} />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
